"use client";

import { ImageIcon, ZoomIn } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export function ProductGallery({
  images,
  title,
  activeImage,
  onSelectImage
}: {
  images: string[];
  title: string;
  activeImage?: string;
  onSelectImage?: (image: string) => void;
}) {
  const [internalActive, setInternalActive] = useState(images[0] || "");
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Image zoom state & refs
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (activeImage) {
      setInternalActive(activeImage);
    }
  }, [activeImage]);

  // Clean up timers & animation frames on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  // Determine current active image, falling back if the current one failed to load
  let active = activeImage || internalActive || images[0] || "";
  if (failedImages.has(active)) {
    const fallback = images.find((img) => !failedImages.has(img));
    if (fallback) {
      active = fallback;
    }
  }

  function handleSelectThumbnail(image: string) {
    if (active === image) return;
    setIsZoomed(false);
    setZoomPos({ x: 50, y: 50 });
    setInternalActive(image);
    onSelectImage?.(image);
  }

  function handleImageError(src: string) {
    setFailedImages((prev) => {
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }

  // Mouse event handlers for smooth image magnification
  function handleMouseEnter() {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    setIsZoomed(true);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX;
    const clientY = e.clientY;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      // Calculate coordinates relative to container (0% to 100%)
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      setZoomPos({ x, y });
    });
  }

  function handleMouseLeave() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setIsZoomed(false);
    // Maintain the exit focal position while the scale-down animation runs (300ms),
    // then smoothly reset origin to center so subsequent entries start clean
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setZoomPos({ x: 50, y: 50 });
    }, 320);
  }

  const isCurrentFailed = failedImages.has(active) && !images.some((img) => !failedImages.has(img));

  return (
    <div className="grid gap-2 lg:grid-cols-[64px_1fr]">
      {/* Thumbnail Bar */}
      {images.length > 1 && (
        <div className="order-2 flex gap-2 overflow-x-auto py-1 no-scrollbar touch-pan-x snap-x snap-mandatory lg:order-1 lg:flex-col lg:overflow-y-auto">
          {images.map((image, index) => {
            const hasFailed = failedImages.has(image);
            return (
              <button
                type="button"
                key={`${image}-${index}`}
                onClick={() => handleSelectThumbnail(image)}
                onMouseEnter={() => handleSelectThumbnail(image)}
                onFocus={() => handleSelectThumbnail(image)}
                className={cn(
                  "relative h-14 w-14 shrink-0 overflow-hidden rounded-[6px] border bg-white transition cursor-pointer snap-start touch-manipulation",
                  active === image
                    ? "border-amazon-orange ring-2 ring-amazon-orange"
                    : "border-slate-200 hover:border-slate-400 dark:border-white/10"
                )}
                aria-label={`View image ${index + 1}`}
              >
                {!hasFailed ? (
                  <Image
                    src={image}
                    alt={`${title} thumbnail ${index + 1}`}
                    fill
                    sizes="56px"
                    className="object-contain p-0.5"
                    onError={() => handleImageError(image)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-slate-50 dark:bg-slate-800">
                    <ImageIcon className="h-4 w-4 text-slate-400" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Main Product Image Container with Magnifier Zoom */}
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group relative order-1 aspect-square overflow-hidden rounded-[8px] bg-white lg:order-2 dark:bg-slate-900 border border-slate-200/90 dark:border-white/10 cursor-crosshair select-none"
      >
        {active && !isCurrentFailed ? (
          <>
            {/* Inner Zooming Image Wrapper:
                - pointer-events-none prevents child elements from interrupting mouse tracking or causing flickering
                - scale(2.25) magnifies within the container's overflow-hidden boundary
                - transformOrigin dynamically matches the mouse coordinate percentage
            */}
            <div
              className="relative h-full w-full pointer-events-none will-change-transform"
              style={{
                transform: isZoomed ? "scale(2.25)" : "scale(1)",
                transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                transition: isZoomed
                  ? "transform 0.2s cubic-bezier(0.2, 0, 0.2, 1)"
                  : "transform 0.3s cubic-bezier(0.2, 0, 0.2, 1)",
              }}
            >
              <Image
                src={active}
                alt={title}
                fill
                priority
                sizes="(max-width: 1024px) 100vw, 40vw"
                className="object-contain p-2 select-none"
                onError={() => handleImageError(active)}
              />
            </div>

            {/* Mobile Image Index Counter */}
            {images.length > 1 && (
              <div className="sm:hidden absolute top-2.5 right-2.5 z-10 rounded-full bg-slate-900/70 px-2 py-0.5 text-[11px] font-bold text-white shadow-xs backdrop-blur-xs">
                {Math.max(1, images.indexOf(active) + 1)} / {images.length}
              </div>
            )}

            {/* Subtle Zoom Indicator Hint Badge */}
            <div
              className={cn(
                "pointer-events-none absolute bottom-3 right-3 z-10 hidden sm:flex items-center gap-1.5 rounded-full bg-slate-900/75 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm backdrop-blur-xs transition-opacity duration-200",
                isZoomed ? "opacity-0" : "opacity-80 group-hover:opacity-100"
              )}
            >
              <ZoomIn className="h-3.5 w-3.5" />
              <span>Roll over image to zoom</span>
            </div>
          </>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-slate-50 dark:bg-slate-800">
            <ImageIcon className="h-16 w-16 text-slate-300 dark:text-slate-600" />
            <span className="text-xs text-slate-400">Image not available</span>
          </div>
        )}
      </div>
    </div>
  );
}

